import { MissingRefreshTokenError } from "@auth0/auth0-spa-js";

import tokenManager from "@/utils/tokenManager";
import { getClient } from "./auth0Client";
import { isAuth0Enabled } from "./authConfig";
import {
  getAuthStatus,
  isNavigationLatched,
  isTerminal,
  setAuthStatus,
} from "./authState";

/**
 * Errors that mean "this user must interact with Auth0 again". Anything else
 * (network, timeout, config) is a real failure and must not silently redirect.
 */
const LOGIN_REQUIRED_CODES = new Set([
  "login_required",
  "consent_required",
  "interaction_required",
  "mfa_required",
]);

export const isLoginRequiredError = (error: unknown): boolean => {
  if (error instanceof MissingRefreshTokenError) return true;
  const code = (error as { error?: string } | null)?.error;
  return typeof code === "string" && LOGIN_REQUIRED_CODES.has(code);
};

/** Strips the auth params so a replayed `returnTo` cannot re-enter the callback. */
export const currentReturnTo = (): string => {
  const { pathname, search, hash } = window.location;
  const params = new URLSearchParams(search);
  for (const key of ["code", "state", "error", "error_description"]) {
    params.delete(key);
  }
  const query = params.toString();
  return `${pathname}${query ? `?${query}` : ""}${hash}`;
};

/**
 * Sends the user to Auth0. Latches `redirecting` rather than clearing a guard
 * in a `finally`: `loginWithRedirect` resolves once navigation has been
 * *initiated*, and the document stays alive for some time after that, so a
 * finally-cleared guard would admit a second redirect before unload.
 *
 * Never redirects out of a terminal or already-navigating state -- a blocked
 * principal cannot be fixed by logging in again, and a logout in flight must
 * not be turned back into a login.
 */
export const requestLogin = async (returnTo?: string): Promise<void> => {
  if (isNavigationLatched() || isTerminal()) return;

  setAuthStatus({ phase: "redirecting" });
  try {
    const client = await getClient();
    await client.loginWithRedirect({
      appState: { returnTo: returnTo ?? currentReturnTo() },
    });
  } catch (error) {
    setAuthStatus({
      phase: "failed",
      reason: "CONFIG_INVALID",
      detail: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

const bearer = (token: string): string => `Bearer ${token}`;

const acquire = async (cacheMode: "on" | "off"): Promise<string | null> => {
  if (isNavigationLatched() || isTerminal()) return null;

  const client = await getClient();
  try {
    return bearer(await client.getTokenSilently({ cacheMode }));
  } catch (error) {
    if (isLoginRequiredError(error)) {
      await requestLogin();
      return null;
    }
    throw error;
  }
};

/**
 * The single seam every caller uses. The two modes deliberately differ in
 * scheme: the legacy backend expects the bare token with no scheme, which is
 * what `endpoints/config.ts` has always sent. Callers must not add a prefix of
 * their own.
 */
export const getAuthHeader = async (): Promise<string | null> => {
  if (!isAuth0Enabled()) return tokenManager.getToken();
  return acquire("on");
};

let inFlightRefresh: Promise<string | null> | null = null;

/**
 * For the 401 refresh-and-retry. Pass the bare token that just failed so a
 * sibling 401's rotation can be reused: if the cached token already differs
 * from the rejected one, another request refreshed it and we return it with no
 * new grant. Otherwise a single shared refresh is forced — spa-js does not
 * dedupe `cacheMode:'off'` grants (its in-lock cache re-read is skipped for
 * 'off'), so N concurrent 401s would trigger N sequential rotations without the
 * single-flight below. Called with no argument it forces a refresh directly.
 */
export const getFreshAuthHeader = async (
  rejectedToken?: string,
): Promise<string | null> => {
  if (!isAuth0Enabled()) return tokenManager.getToken();
  if (isNavigationLatched() || isTerminal()) return null;

  const client = await getClient();

  if (rejectedToken) {
    try {
      const cached = await client.getTokenSilently({ cacheMode: "on" });
      if (cached !== rejectedToken) return bearer(cached);
    } catch (error) {
      if (!isLoginRequiredError(error)) throw error;
    }
  }

  if (!inFlightRefresh) {
    inFlightRefresh = (async () => {
      try {
        return bearer(await client.getTokenSilently({ cacheMode: "off" }));
      } catch (error) {
        if (isLoginRequiredError(error)) {
          await requestLogin();
          return null;
        }
        throw error;
      } finally {
        inFlightRefresh = null;
      }
    })();
  }
  return inFlightRefresh;
};

export const isAuthenticated = async (): Promise<boolean> => {
  if (!isAuth0Enabled()) return tokenManager.hasToken();
  if (isTerminal()) return false;
  const client = await getClient();
  return client.isAuthenticated();
};

export const currentPhase = () => getAuthStatus().phase;
